/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/require-default-props */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DatePicker, DateTimePicker, TimePicker } from '@progress/kendo-react-dateinputs';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { Button } from '../../../../atoms/Button/Button';
import { Label } from '../../../../atoms/Label';
import { TextInput } from '../TextInput/TextInput';
import './DateInputStyles.scss';
dayjs.extend(customParseFormat);
const DEFAULT_DATE_FORMAT = 'MM/dd/yyyy';
let generatedDateInputId = 0;
const nextDateInputId = () => {
    generatedDateInputId += 1;
    return `qmb-date-input-${generatedDateInputId}`;
};
const KENDO_TOKEN_TO_DAYJS = {
    EEEE: 'dddd',
    EEE: 'ddd',
    EE: 'ddd',
    E: 'ddd',
    yyyy: 'YYYY',
    yyy: 'YYYY',
    yy: 'YY',
    y: 'YYYY',
    MMMM: 'MMMM',
    MMM: 'MMM',
    MM: 'MM',
    M: 'M',
    dd: 'DD',
    d: 'D',
    HH: 'HH',
    H: 'H',
    hh: 'hh',
    h: 'h',
    mm: 'mm',
    m: 'm',
    ss: 'ss',
    s: 's',
    a: 'a'
};
const KENDO_TOKEN_LETTERS = new Set(Object.keys(KENDO_TOKEN_TO_DAYJS).map((token) => token[0]));
/**
 * Convert a Kendo / CLDR date format to a Day.js format.
 * Token runs are mapped as a whole so `EEEE` becomes `dddd` and `y` becomes `YYYY`,
 * instead of a character-level d/y swap that leaves weekday tokens invalid.
 */
export function kendoFormatToDayjs(format) {
    var _a;
    let index = 0;
    let converted = '';
    while (index < format.length) {
        const char = format[index];
        if (char === "'") {
            const closing = format.indexOf("'", index + 1);
            if (closing === -1) {
                converted += `[${format.slice(index + 1)}]`;
                break;
            }
            converted += `[${format.slice(index + 1, closing)}]`;
            index = closing + 1;
            continue;
        }
        if (KENDO_TOKEN_LETTERS.has(char)) {
            let end = index + 1;
            while (end < format.length && format[end] === char)
                end += 1;
            const token = format.slice(index, end);
            converted += (_a = KENDO_TOKEN_TO_DAYJS[token]) !== null && _a !== void 0 ? _a : token;
            index = end;
            continue;
        }
        converted += char;
        index += 1;
    }
    return converted;
}
const NUMERIC_DATE_FIELD = /^(Y+|M+|D+)$/;
/**
 * True when the Day.js format is only year/month/day fields and separators
 * (e.g. `MM/DD/YYYY`). Weekday and other letter tokens stay free-text.
 */
export function isNumericDateFormat(dayjsFormat) {
    const withoutLiterals = dayjsFormat.replace(/\[[^\]]*\]/g, '');
    return withoutLiterals.length > 0 && /^[YMD./\-\s,]+$/.test(withoutLiterals);
}
/**
 * Keep typed digits inside the date mask. `04022026` with `MM/DD/YYYY`
 * becomes `04/02/2026`. Non-digits are stripped.
 */
export function maskDateDigits(raw, dayjsFormat) {
    var _a;
    const format = dayjsFormat.replace(/\[[^\]]*\]/g, '');
    const tokens = (_a = format.match(/Y+|M+|D+|[^YMD]+/g)) !== null && _a !== void 0 ? _a : [];
    const maxDigits = tokens
        .filter((token) => NUMERIC_DATE_FIELD.test(token))
        .reduce((count, token) => count + token.length, 0);
    const digits = raw.replace(/\D/g, '').slice(0, maxDigits);
    if (!digits)
        return '';
    let digitIndex = 0;
    let masked = '';
    for (let tokenIndex = 0; tokenIndex < tokens.length; tokenIndex += 1) {
        const token = tokens[tokenIndex];
        if (!NUMERIC_DATE_FIELD.test(token))
            continue;
        const chunk = digits.slice(digitIndex, digitIndex + token.length);
        if (!chunk)
            break;
        masked += chunk;
        digitIndex += chunk.length;
        if (chunk.length < token.length)
            break;
        const separator = tokens[tokenIndex + 1];
        if (separator && !NUMERIC_DATE_FIELD.test(separator) && digitIndex < maxDigits) {
            masked += separator;
        }
    }
    return masked;
}
const caretAfterDigits = (masked, digitCount) => {
    if (digitCount <= 0)
        return 0;
    let seen = 0;
    for (let index = 0; index < masked.length; index += 1) {
        if (/\d/.test(masked[index])) {
            seen += 1;
            if (seen === digitCount) {
                let position = index + 1;
                while (position < masked.length && /\D/.test(masked[position])) {
                    position += 1;
                }
                return position;
            }
        }
    }
    return masked.length;
};
const formatDateValue = (value, dayjsFormat) => value ? dayjs(value).format(dayjsFormat) : '';
const parseDateDraft = (draft, dayjsFormat) => {
    const trimmed = draft.trim();
    if (!trimmed)
        return null;
    const parsed = dayjs(trimmed, dayjsFormat, true);
    return parsed.isValid() ? parsed.toDate() : null;
};
const isOutsideRange = (date, min, max) => {
    if (min && dayjs(date).isBefore(dayjs(min), 'day'))
        return true;
    if (max && dayjs(date).isAfter(dayjs(max), 'day'))
        return true;
    return false;
};
/**
 * DateInput Component
 * Form date/time input with picker
 * Supports: date, time, datetime
 *
 * Date type uses a Quimby TextInput as the visible field. Kendo DatePicker stays
 * mounted only to own the calendar popup, which opens from the calendar Button.
 * @version 1.2.0
 * @since 2026-02-05
 */
export function DateInput({ type = 'date', id, name, label, required = false, error, disabled = false, value, onChange, placeholder, className, min, max, format = DEFAULT_DATE_FORMAT, timeFormat = 'h:mm a', popupSettings }) {
    const handleChange = (event) => {
        onChange === null || onChange === void 0 ? void 0 : onChange(event.value);
    };
    const commonProps = Object.assign(Object.assign(Object.assign(Object.assign({ name, value: value || null, onChange: handleChange, disabled,
        placeholder }, (id && { id })), (id && label && { ariaLabelledBy: `${id}-label` })), (!id && label && typeof label === 'string' && { ariaLabel: label })), (popupSettings && { popupSettings }));
    if (type === 'time' || type === 'datetime') {
        return (React.createElement("div", { className: `qmb-ui-input ${className || ''} ${error ? 'has-error' : ''}` },
            type === 'time' ? (React.createElement(TimePicker, Object.assign({}, commonProps, { format: timeFormat }))) : (React.createElement(DateTimePicker, Object.assign({}, commonProps, { format: format, min: min, max: max }))),
            label && (React.createElement(Label, { id: id ? `${id}-label` : undefined, className: `qmb-ui-label ${required ? '--required' : ''}`, htmlFor: id }, label)),
            error && React.createElement("span", { className: "error-message" }, error)));
    }
    return (React.createElement(QuimbyDateField, { id: id, name: name, label: label, required: required, error: error, disabled: disabled, value: value !== null && value !== void 0 ? value : null, onChange: onChange, placeholder: placeholder, className: className, min: min, max: max, format: format, popupSettings: popupSettings }));
}
const QuimbyDateField = ({ id, name, label, required, error, disabled, value, onChange, placeholder, className, min, max, format, popupSettings }) => {
    const generatedIdRef = useRef(id || nextDateInputId());
    const inputId = id || generatedIdRef.current;
    const inputRef = useRef(null);
    const dayjsFormat = kendoFormatToDayjs(format);
    const numericMask = isNumericDateFormat(dayjsFormat);
    const [draft, setDraft] = useState(() => formatDateValue(value, dayjsFormat));
    const [calendarOpen, setCalendarOpen] = useState(false);
    const lastCommittedRef = useRef(value);
    const labelText = typeof label === 'string' ? label : '';
    useEffect(() => {
        lastCommittedRef.current = value;
        setDraft(formatDateValue(value, dayjsFormat));
    }, [value, dayjsFormat]);
    const resolvedPopupSettings = Object.assign({ appendTo: typeof document === 'undefined' ? undefined : document.body }, popupSettings);
    const toggleCalendar = useCallback(() => {
        if (!disabled)
            setCalendarOpen((open) => !open);
    }, [disabled]);
    const handleDraftChange = (event) => {
        var _a;
        const { value: nextRaw } = event.target;
        if (!numericMask) {
            setDraft(nextRaw);
            return;
        }
        const selectionStart = (_a = event.target.selectionStart) !== null && _a !== void 0 ? _a : nextRaw.length;
        const digitsBefore = nextRaw.slice(0, selectionStart).replace(/\D/g, '').length;
        const nextDraft = maskDateDigits(nextRaw, dayjsFormat);
        setDraft(nextDraft);
        requestAnimationFrame(() => {
            const node = inputRef.current;
            if (!node)
                return;
            const position = caretAfterDigits(nextDraft, digitsBefore);
            node.setSelectionRange(position, position);
        });
    };
    const commitDraft = useCallback((nextDraft) => {
        const trimmed = nextDraft.trim();
        if (!trimmed) {
            if (lastCommittedRef.current !== null) {
                onChange === null || onChange === void 0 ? void 0 : onChange(null);
            }
            setDraft('');
            return;
        }
        const parsed = parseDateDraft(trimmed, dayjsFormat);
        if (parsed && !isOutsideRange(parsed, min, max)) {
            onChange === null || onChange === void 0 ? void 0 : onChange(parsed);
            setDraft(formatDateValue(parsed, dayjsFormat));
            return;
        }
        setDraft(formatDateValue(lastCommittedRef.current, dayjsFormat));
    }, [dayjsFormat, max, min, onChange]);
    const handlePickerChange = (event) => {
        onChange === null || onChange === void 0 ? void 0 : onChange(event.value);
        setCalendarOpen(false);
    };
    return (React.createElement("div", { className: ['qmb-ui-date-input', className].filter(Boolean).join(' ') },
        React.createElement(TextInput, { ref: inputRef, id: inputId, name: name, label: labelText, value: draft, onChange: handleDraftChange, onBlur: () => commitDraft(draft), placeholder: placeholder && placeholder.trim() ? placeholder : dayjsFormat, required: required, disabled: disabled, error: error, autoComplete: "off", inputMode: numericMask ? 'numeric' : undefined }),
        React.createElement(Button, { type: "button", className: "qmb-ui-date-input__calendar-button", "aria-label": "Open calendar", "aria-expanded": calendarOpen, disabled: disabled, onMouseDown: (event) => {
                event.preventDefault();
            }, onClick: toggleCalendar },
            React.createElement("i", { className: "fa-light fa-calendar-pen", "aria-hidden": "true" })),
        label && typeof label !== 'string' && (React.createElement(Label, { id: `${inputId}-label`, className: `qmb-ui-label ${required ? '--required' : ''}`, htmlFor: inputId }, label)),
        React.createElement("div", { className: "qmb-ui-date-input__kendo", "aria-hidden": "true" },
            React.createElement(DatePicker, { name: name, value: value, onChange: handlePickerChange, format: format, min: min, max: max, disabled: disabled, show: calendarOpen, onClose: () => setCalendarOpen(false), popupSettings: resolvedPopupSettings, tabIndex: -1 }))));
};
DateInput.displayName = 'DateInput';
